import {
    Drawer,
    DrawerOverlay,
    DrawerContent,
    Box,
    Text,
    VStack,
    HStack,
    Flex,
    IconButton,
    Icon,
    Textarea,
    Button,
    useToast,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    Portal,
    Tag,
    TagLabel,
    TagCloseButton,
    Wrap,
    WrapItem,
    Alert,
    AlertIcon,
    CloseButton,
    Tooltip,
} from '@chakra-ui/react';
import React, { useState, useRef, useEffect, useContext, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ResizeTextarea from 'react-textarea-autosize';
import { TbMessageCircle, TbTruck, TbPencil, TbCheck, TbX, TbArrowUp, TbTrash, TbNote, TbPinnedFilled } from 'react-icons/tb';
import { Icon as YaxleIcon } from '../ui';
import { OrderNote, SupplierNote } from '../hooks/useGetOrderDetail';
import { useUpdateOrderDetails } from '../hooks/useUpdateOrderDetails';
import {
    useGetMentionableUsers,
    MentionableUser,
    buildMayNotRespondSummary,
} from '../hooks/useGetMentionableUsers';
import SessionContext from '../contexts/SessionContext';
import PresenceAvatar from '../components/chat/PresenceAvatar';
import { useForceSidebarCollapsed } from '../contexts/SidebarCollapseOverrideContext';
import { SIDE_DRAWER_CONTAINER_PROPS, INSTANT_SIDE_DRAWER_MOTION_PROPS } from './sideDrawerContainerProps';
import { note as noteColor } from '../ui/theme/tokens';
import { positionLabel } from '../utils/invitePositionOptions';

const DISCLAIMER_DISMISS_STORAGE_KEY = 'chat-disclaimer-dismissed-by-day';

function localDayKey(d = new Date()): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Dismissals are per user for the local calendar day, across all order threads. */
function readDismissedDisclaimerIds(): Set<string> {
    try {
        const raw = localStorage.getItem(DISCLAIMER_DISMISS_STORAGE_KEY);
        if (!raw) return new Set();
        const parsed = JSON.parse(raw) as { day?: string; userIds?: unknown };
        if (parsed?.day !== localDayKey() || !Array.isArray(parsed.userIds)) return new Set();
        return new Set(parsed.userIds.filter((id): id is string => typeof id === 'string'));
    } catch {
        return new Set();
    }
}

function writeDismissedDisclaimerIds(ids: Set<string>) {
    try {
        const list: string[] = [];
        ids.forEach((id) => { list.push(id); });
        localStorage.setItem(
            DISCLAIMER_DISMISS_STORAGE_KEY,
            JSON.stringify({ day: localDayKey(), userIds: list }),
        );
    } catch {
        // ignore quota / private mode
    }
}

export interface OrderChatPanelProps {
    orderId: string;
    mode: 'order' | 'supplier';
    notes?: Array<OrderNote>;
    supplierNotes?: Array<SupplierNote>;
    /** Initial compose mode: 'message' (notifies/mentions) or 'note' (private note to self). */
    initialComposeMode?: 'message' | 'note';
    refetch?: () => void;
    onClose?: () => void;
    /** When true, the panel is embedded (no close button in header) */
    embedded?: boolean;
    /** When true, hides the panel's own "Messages (N)" header (e.g. when a parent provides one) */
    hideHeader?: boolean;
    /** Thread background. Drawers use the inverted white body; the full-page
     *  /chat route passes gray so internal threads match the vendor pane. */
    threadBg?: string;
    /** Display names to highlight as @mentions — pass from order detail to avoid the
     *  mentionable-users fetch delay. Falls back to the fetched mentionable users. */
    mentionNames?: string[];
}

interface OrderChatDrawerProps extends OrderChatPanelProps {
    isOpen: boolean;
    onClose: () => void;
    /** When true, hides the DrawerOverlay (used when nested inside another drawer) */
    hideOverlay?: boolean;
}

/** Render message text with @mentions highlighted by matching against known user names. */
const renderNoteText = (text: string, knownNames: string[], mentionColor = '#0071E3') => {
    if (knownNames.length === 0) return text;

    const sortedNames = [...knownNames].sort((a, b) => b.length - a.length);
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
        const atIndex = remaining.indexOf('@');
        if (atIndex === -1) {
            parts.push(remaining);
            break;
        }

        if (atIndex > 0) parts.push(remaining.slice(0, atIndex));

        const afterAt = remaining.slice(atIndex + 1);
        const matchedName = sortedNames.find(name => afterAt.startsWith(name));

        if (matchedName) {
            parts.push(
                <Text
                    as="span"
                    key={key++}
                    color={mentionColor}
                    fontWeight="semibold"
                    fontSize="sm"
                >
                    @{matchedName}
                </Text>
            );
            remaining = remaining.slice(atIndex + 1 + matchedName.length);
        } else {
            parts.push('@');
            remaining = afterAt;
        }
    }
    return parts.length > 0 ? parts : text;
};

/** Reusable chat panel content -- can be embedded inline or wrapped in a Drawer */
export const OrderChatPanel = ({
    orderId,
    mode,
    notes = [],
    supplierNotes = [],
    initialComposeMode = 'message',
    refetch,
    onClose,
    embedded = false,
    hideHeader = false,
    threadBg = 'white',
    mentionNames,
}: OrderChatPanelProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const updateOrderDetails = useUpdateOrderDetails();
    const sessionContext = useContext(SessionContext);
    const toast = useToast();
    const notesEndRef = useRef<HTMLDivElement>(null);
    const noteInputRef = useRef<HTMLTextAreaElement>(null);
    /** Hide when already viewing this thread on the full Chats page. */
    const showOpenInChats = location.pathname !== '/chat';

    /** Continue this order thread on the full Chats page. */
    const openInChats = () => {
        onClose?.();
        navigate('/chat', { state: { newChat: { mode: 'order' as const, orderId } } });
    };

    const [newNoteText, setNewNoteText] = useState('');
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [editingNoteText, setEditingNoteText] = useState('');
    const [composeMode, setComposeMode] = useState<'message' | 'note'>(initialComposeMode);
    const [showMentionMenu, setShowMentionMenu] = useState(false);
    const [mentionFilter, setMentionFilter] = useState('');
    const [mentionCursorPos, setMentionCursorPos] = useState(0);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const [mentionedUsers, setMentionedUsers] = useState<MentionableUser[]>([]);
    /** Track mention userIds inserted during current compose session */
    const pendingMentionIds = useRef<string[]>([]);

    const { data: mentionableUsers } = useGetMentionableUsers(orderId);

    // Names used to highlight @mentions. Prefer `mentionNames` (passed in with the
    // order detail) so bolding is instant; otherwise fall back to the async mentionable
    // users. Always include the current user.
    const mentionHighlightNames = useMemo(() => {
        const names = (mentionNames && mentionNames.length > 0)
            ? [...mentionNames]
            : (mentionableUsers ?? []).map(u => u.displayName);
        const self = `${sessionContext?.firstName ?? ''} ${sessionContext?.lastName ?? ''}`.trim();
        if (self && !names.includes(self)) names.push(self);
        return names;
    }, [mentionNames, mentionableUsers, sessionContext?.firstName, sessionContext?.lastName]);

    const [dismissedDisclaimerIds, setDismissedDisclaimerIds] = useState<Set<string>>(
        () => readDismissedDisclaimerIds(),
    );

    // Refresh day-scoped dismissals when opening a different thread (handles midnight rollover).
    useEffect(() => {
        setDismissedDisclaimerIds(readDismissedDisclaimerIds());
    }, [orderId]);

    const presenceByUserId = useMemo(() => {
        const map = new Map<string, MentionableUser>();
        for (const u of mentionableUsers ?? []) {
            map.set(u._id, u);
        }
        return map;
    }, [mentionableUsers]);

    const mayNotRespondSummary = useMemo(() => {
        if (mode !== 'order') return null;
        const visible = (mentionableUsers ?? []).filter(
            u =>
                u.mayNotRespond &&
                !dismissedDisclaimerIds.has(u._id) &&
                u._id !== sessionContext?.userId,
        );
        return buildMayNotRespondSummary(visible);
    }, [mode, mentionableUsers, dismissedDisclaimerIds, sessionContext?.userId]);

    const dismissAllDisclaimers = useCallback((userIds: string[]) => {
        setDismissedDisclaimerIds(prev => {
            const next = new Set(prev);
            userIds.forEach(id => next.add(id));
            writeDismissedDisclaimerIds(next);
            return next;
        });
    }, []);

    const noteCount = mode === 'order' ? notes.length : supplierNotes.length;

    // Reset compose mode when initialComposeMode changes (e.g. opened via "Add Note to Self")
    useEffect(() => {
        setComposeMode(initialComposeMode);
    }, [initialComposeMode]);

    // Scroll to bottom and focus input on mount
    useEffect(() => {
        if (mode === 'order') {
            setTimeout(() => {
                notesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                noteInputRef.current?.focus();
            }, 300);
        }
    }, [mode]);

    // Scroll when new notes arrive
    useEffect(() => {
        if (notesEndRef.current) {
            notesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [notes.length]);

    const formatNoteDate = (dateString: string) =>
        new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });

    /** Time-only, shown under each message bubble. */
    const formatMessageTime = (dateString: string) =>
        new Date(dateString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    /** Centered day divider between message groups. */
    const formatDayDivider = (dateString: string) => {
        const d = new Date(dateString);
        const sameYear = d.getFullYear() === new Date().getFullYear();
        return d.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            ...(sameYear ? {} : { year: 'numeric' }),
        });
    };

    const isSameDay = (a: string, b: string) => {
        const da = new Date(a);
        const db = new Date(b);
        return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
    };

    const filteredMentionUsers = useMemo(() => {
        if (!mentionableUsers) return [];
        const others = mentionableUsers.filter(u => u._id !== sessionContext?.userId);
        if (!mentionFilter) return others;
        const lower = mentionFilter.toLowerCase();
        return others.filter(u =>
            u.displayName.toLowerCase().includes(lower)
        );
    }, [mentionableUsers, mentionFilter, sessionContext?.userId]);

    // Reset highlight index when the filtered list changes
    useEffect(() => {
        setSelectedMentionIndex(0);
    }, [mentionFilter]);

    const handleMentionSelect = useCallback((user: MentionableUser) => {
        const text = newNoteText;
        const beforeCursor = text.slice(0, mentionCursorPos);
        const atIndex = beforeCursor.lastIndexOf('@');
        if (atIndex === -1) return;

        // Strip the @filter text from the textarea
        const before = text.slice(0, atIndex);
        const after = text.slice(mentionCursorPos);
        setNewNoteText(before + after);

        // Add user as a tag (avoid duplicates)
        setMentionedUsers(prev =>
            prev.some(u => u._id === user._id) ? prev : [...prev, user]
        );

        if (!pendingMentionIds.current.includes(user._id)) {
            pendingMentionIds.current.push(user._id);
        }

        setShowMentionMenu(false);
        setMentionFilter('');
        noteInputRef.current?.focus();
    }, [newNoteText, mentionCursorPos]);

    const removeMention = useCallback((userId: string) => {
        setMentionedUsers(prev => prev.filter(u => u._id !== userId));
        pendingMentionIds.current = pendingMentionIds.current.filter(id => id !== userId);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const cursorPos = e.target.selectionStart || 0;
        setNewNoteText(value);

        // @mentions only apply to messages, not private notes to self.
        if (composeMode === 'message') {
            const beforeCursor = value.slice(0, cursorPos);
            const atMatch = beforeCursor.match(/@(\w*)$/);
            if (atMatch) {
                setShowMentionMenu(true);
                setMentionFilter(atMatch[1]);
                setMentionCursorPos(cursorPos);
            } else {
                setShowMentionMenu(false);
                setMentionFilter('');
            }
        }
    };

    const handleAddNote = async () => {
        if ((!newNoteText.trim() && mentionedUsers.length === 0) || !orderId) return;

        const noteType = composeMode;
        // Build final note text: prepend @mentions from tags, then the typed text
        const mentionPrefix = mentionedUsers.map(u => `@${u.displayName}`).join(' ');
        const finalNote = mentionPrefix
            ? `${mentionPrefix} ${newNoteText.trim()}`
            : newNoteText.trim();

        // Notes to self are private: never attach mentions / notify recipients.
        // @mention eligibility (the picker / `mentionableUsers`) is anyone with access
        // to the order's location, regardless of position. Implicit auto-notify on send
        // is a separate list: central-parts, parts-managers, and executives assigned to that location.
        const explicitMentionIds = noteType === 'message' ? mentionedUsers.map(u => u._id) : [];
        const implicitRecipientIds = noteType === 'message'
            ? (mentionableUsers ?? [])
                .filter(u => (u.role === 'central-parts' || u.role === 'parts-manager' || u.role === 'executive') && u._id !== sessionContext?.userId)
                .map(u => u._id)
            : [];
        const mentionedUserIds = Array.from(new Set([...explicitMentionIds, ...implicitRecipientIds]));

        try {
            await updateOrderDetails.mutateAsync({
                orderId,
                note: finalNote,
                noteType,
                ...(mentionedUserIds.length > 0 ? { mentionedUserIds } : {}),
                ...(explicitMentionIds.length > 0 ? { explicitMentionIds } : {}),
            });
            setNewNoteText('');
            setMentionedUsers([]);
            setShowMentionMenu(false);
            pendingMentionIds.current = [];
            refetch?.();
            if (noteType !== 'message') {
                toast({
                    title: 'Note added',
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });
            }
        } catch {
            toast({
                title: noteType === 'message' ? 'Failed to send message' : 'Failed to add note',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleEditNote = async () => {
        if (!editingNoteText.trim() || !orderId || !editingNoteId) return;

        try {
            await updateOrderDetails.mutateAsync({
                orderId,
                editNote: {
                    _id: editingNoteId,
                    newText: editingNoteText.trim(),
                },
            });
            setEditingNoteId(null);
            setEditingNoteText('');
            refetch?.();
            toast({
                title: 'Updated',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
        } catch {
            toast({
                title: 'Failed to update',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleDeleteNote = async (noteId: string, noteType: 'note' | 'message') => {
        if (!orderId) return;

        try {
            await updateOrderDetails.mutateAsync({
                orderId,
                deleteNoteId: noteId,
            });
            refetch?.();
            toast({
                title: noteType === 'message' ? 'Message deleted' : 'Note deleted',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
        } catch {
            toast({
                title: 'Failed to delete',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const startEditingNote = (noteId: string, currentText: string) => {
        setEditingNoteId(noteId);
        setEditingNoteText(currentText);
    };

    const cancelEditingNote = () => {
        setEditingNoteId(null);
        setEditingNoteText('');
    };

    const handleNoteKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, isEditing: boolean) => {
        // Mention menu keyboard navigation
        if (showMentionMenu && !isEditing && filteredMentionUsers.length > 0) {
            if (e.key === 'Tab' || (e.key === 'Enter' && !e.metaKey && !e.ctrlKey)) {
                e.preventDefault();
                handleMentionSelect(filteredMentionUsers[selectedMentionIndex]);
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedMentionIndex(prev =>
                    prev < filteredMentionUsers.length - 1 ? prev + 1 : 0
                );
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedMentionIndex(prev =>
                    prev > 0 ? prev - 1 : filteredMentionUsers.length - 1
                );
                return;
            }
        }

        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            if (isEditing) {
                handleEditNote();
            } else {
                handleAddNote();
            }
        } else if (e.key === 'Escape') {
            if (showMentionMenu) {
                setShowMentionMenu(false);
            } else if (isEditing) {
                cancelEditingNote();
            } else {
                setNewNoteText('');
            }
        }
    };

    const isSupplierMode = mode === 'supplier';
    const messageCount = notes.filter(n => (n.type ?? 'message') === 'message').length;
    const selfNoteCount = notes.filter(n => (n.type ?? 'message') === 'note').length;
    const headerIcon = isSupplierMode ? TbTruck : composeMode === 'note' ? TbPinnedFilled : TbMessageCircle;
    // ENG-2147: count renders as a grey circle badge next to the title, not in parentheses.
    const headerTitle = isSupplierMode
        ? 'Supplier Notes'
        : composeMode === 'note'
            ? 'Notes to Self'
            : 'Messages';
    const headerCount = isSupplierMode ? noteCount : composeMode === 'note' ? selfNoteCount : messageCount;
    const headerColor = isSupplierMode ? 'orange.500' : composeMode === 'note' ? noteColor.ink : 'gray.600';
    const sharedMessageCardStyles = {
        bg: 'white',
        p: 3,
        borderRadius: 'lg',
        borderWidth: '1px',
        borderColor: 'border.default',
    } as const;

    return (
                <Box
                    h="100%"
                    bg={threadBg}
                    display="flex"
                    flexDirection="column"
                >
                    {/* Header */}
                    {!hideHeader && (
                    <Box
                        h="topbar"
                        px={4}
                        bg="white"
                        borderBottomWidth="1px"
                        borderColor="border.subtle"
                        display="flex"
                        alignItems="center"
                        flexShrink={0}
                    >
                        <Flex align="center" justify="space-between" w="100%">
                            <HStack spacing={2}>
                                <Icon as={headerIcon as React.ElementType} w={5} h={5} color={headerColor} />
                                <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                    {headerTitle}
                                </Text>
                                <Flex
                                    bg="gray.200"
                                    borderRadius="full"
                                    h="20px"
                                    minW="20px"
                                    px="6px"
                                    align="center"
                                    justify="center"
                                >
                                    <Text fontSize="11px" fontWeight="bold" lineHeight="1" color="gray.700">
                                        {headerCount}
                                    </Text>
                                </Flex>
                            </HStack>
                            <HStack spacing={1}>
                                {showOpenInChats && !isSupplierMode && (
                                    <Tooltip label="Open in Chats" placement="bottom">
                                        <IconButton
                                            aria-label="Open in Chats"
                                            icon={<YaxleIcon name="expand" size="sm" />}
                                            size="sm"
                                            variant="ghost"
                                            color="fg.muted"
                                            onClick={openInChats}
                                        />
                                    </Tooltip>
                                )}
                                {onClose && (
                                    <CloseButton onClick={onClose} size="sm" />
                                )}
                            </HStack>
                        </Flex>
                    </Box>
                    )}

                    {mayNotRespondSummary && (
                        <Box px={4} pt={3} pb={0} flexShrink={0}>
                            <Alert
                                status="warning"
                                variant="left-accent"
                                borderRadius="md"
                                py={2}
                                pl={3}
                                pr={8}
                                fontSize="xs"
                                alignItems="flex-start"
                                position="relative"
                            >
                                <AlertIcon boxSize="14px" mt="1px" />
                                <VStack spacing={1} align="stretch" pr={1}>
                                    {mayNotRespondSummary.inactiveLine && (
                                        <Text fontSize="xs" color="gray.700" lineHeight="1.4">
                                            {mayNotRespondSummary.inactiveLine}
                                        </Text>
                                    )}
                                    {mayNotRespondSummary.awayLines.map((line, i) => (
                                        <Text key={i} fontSize="xs" color="gray.700" lineHeight="1.4">
                                            {line}
                                        </Text>
                                    ))}
                                </VStack>
                                <CloseButton
                                    size="sm"
                                    position="absolute"
                                    top={1}
                                    right={1}
                                    aria-label="Dismiss disclaimer"
                                    onClick={() => dismissAllDisclaimers(mayNotRespondSummary.userIds)}
                                />
                            </Alert>
                        </Box>
                    )}

                    {/* Chat List */}
                    <VStack
                        flex="1"
                        overflow="auto"
                        px={5}
                        py={5}
                        spacing={4}
                        align="stretch"
                    >
                        {isSupplierMode ? (
                            /* Supplier Notes - Read Only */
                            supplierNotes.length > 0 ? (
                                supplierNotes.map((note) => (
                                    <Box
                                        key={note._id}
                                        {...sharedMessageCardStyles}
                                    >
                                        <HStack justify="space-between" mb={1}>
                                            <Text fontSize="xs" fontWeight="semibold" color="orange.500">
                                                {note.supplierName}
                                            </Text>
                                            <Text fontSize="xs" color="gray.400">
                                                {formatNoteDate(note.timeReceived)}
                                            </Text>
                                        </HStack>
                                        <Text
                                            fontSize="sm"
                                            color="gray.700"
                                            whiteSpace="pre-wrap"
                                            lineHeight="1.5"
                                        >
                                            {note.question}
                                        </Text>
                                    </Box>
                                ))
                            ) : (
                                <Flex flex="1" align="center" justify="center" direction="column" py={8}>
                                    <Icon as={TbTruck as React.ElementType} w={8} h={8} color="gray.300" mb={2} />
                                    <Text fontSize="sm" color="gray.400" textAlign="center">
                                        No supplier notes
                                    </Text>
                                </Flex>
                            )
                        ) : composeMode === 'note' ? (
                            /* Notes to Self View */
                            (() => {
                                const selfNotes = [...notes].filter(n => (n.type ?? 'message') === 'note')
                                    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                                return selfNotes.length > 0 ? (
                                    selfNotes.map((note, index) => {
                                        const isEditing = editingNoteId === note._id;
                                        const rotation = index % 2 === 0 ? '1deg' : '-1deg';
                                        return (
                                            <Box
                                                key={`${note.timestamp}-${index}`}
                                                bg={noteColor.solid}
                                                p={5}
                                                pt={7}
                                                minH="120px"
                                                borderRadius="2px"
                                                position="relative"
                                                transform={`rotate(${rotation})`}
                                                boxShadow="none"
                                                transition="transform 0.15s ease"
                                                _hover={{ transform: 'rotate(0deg)' }}
                                                _before={{
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: '-6px',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    w: '8px',
                                                    h: '8px',
                                                    borderRadius: '50%',
                                                    bg: '#c0392b',
                                                    boxShadow: 'none',
                                                    zIndex: 1,
                                                }}
                                                _after={{
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: '-2px',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    w: '1.5px',
                                                    h: '10px',
                                                    bg: '#aaa',
                                                    zIndex: 0,
                                                }}
                                            >
                                                {isEditing ? (
                                                    <VStack spacing={2} align="stretch">
                                                        <Textarea
                                                            value={editingNoteText}
                                                            onChange={(e) => setEditingNoteText(e.target.value)}
                                                            onKeyDown={(e) => handleNoteKeyDown(e, true)}
                                                            size="sm"
                                                            resize="none"
                                                            minH="60px"
                                                            fontSize="sm"
                                                            bg="rgba(255,255,255,0.55)"
                                                            color={noteColor.ink}
                                                            borderColor={noteColor.border}
                                                            _focus={{ borderColor: noteColor.deep, bg: 'rgba(255,255,255,0.75)' }}
                                                            _placeholder={{ color: noteColor.muted }}
                                                            autoFocus
                                                            isDisabled={updateOrderDetails.isPending}
                                                        />
                                                        <HStack spacing={2} justify="flex-end">
                                                            <Button size="xs" variant="outlineGray" color={noteColor.ink} _hover={{ bg: 'whiteAlpha.600' }} leftIcon={<Icon as={TbX as React.ElementType} />} onClick={cancelEditingNote} isDisabled={updateOrderDetails.isPending}>
                                                                Cancel
                                                            </Button>
                                                            <Button size="xs" bg={noteColor.ink} color="white" _hover={{ bg: noteColor.inkHover }} leftIcon={<Icon as={TbCheck as React.ElementType} />} onClick={handleEditNote} isLoading={updateOrderDetails.isPending}>
                                                                Save
                                                            </Button>
                                                        </HStack>
                                                    </VStack>
                                                ) : (
                                                    <Text fontSize="sm" color={noteColor.ink} whiteSpace="pre-wrap" lineHeight="1.6">
                                                        {note.note}
                                                    </Text>
                                                )}
                                                <HStack justify="space-between" mt={3} pt={2} borderTopWidth="1px" borderColor="blackAlpha.200">
                                                    <Text fontSize="xs" color={noteColor.muted}>
                                                        {formatNoteDate(note.timestamp.toString())}
                                                    </Text>
                                                    {!isEditing && (
                                                        <HStack spacing={0}>
                                                            <IconButton
                                                                aria-label="Edit note"
                                                                icon={<Icon as={TbPencil as React.ElementType} w={3} h={3} />}
                                                                size="xs"
                                                                variant="ghost"
                                                                color={noteColor.muted}
                                                                minW="auto"
                                                                h="auto"
                                                                p={1}
                                                                onClick={() => startEditingNote(note._id, note.note)}
                                                                _hover={{ color: noteColor.ink }}
                                                            />
                                                            <Popover placement="top" isLazy>
                                                                <PopoverTrigger>
                                                                    <IconButton
                                                                        aria-label="Delete note"
                                                                        icon={<Icon as={TbTrash as React.ElementType} w={3} h={3} />}
                                                                        size="xs"
                                                                        variant="ghost"
                                                                        color={noteColor.muted}
                                                                        minW="auto"
                                                                        h="auto"
                                                                        p={1}
                                                                        _hover={{ color: 'red.500' }}
                                                                    />
                                                                </PopoverTrigger>
                                                                <Portal>
                                                                    <PopoverContent w="auto" minW="120px">
                                                                        <PopoverBody p={2}>
                                                                            <VStack spacing={1}>
                                                                                <Text fontSize="xs" color="gray.600">Delete this note?</Text>
                                                                                <HStack spacing={2}>
                                                                                    <Button size="xs" colorScheme="red" onClick={() => handleDeleteNote(note._id, 'note')}>
                                                                                        Delete
                                                                                    </Button>
                                                                                </HStack>
                                                                            </VStack>
                                                                        </PopoverBody>
                                                                    </PopoverContent>
                                                                </Portal>
                                                            </Popover>
                                                        </HStack>
                                                    )}
                                                </HStack>
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <Flex flex="1" align="center" justify="center" direction="column" py={8}>
                                        <Box
                                            bg={noteColor.solid}
                                            p={6}
                                            borderRadius="2px"
                                            boxShadow="none"
                                            transform="rotate(-1deg)"
                                            textAlign="center"
                                            minW="180px"
                                        >
                                            <Icon as={TbPinnedFilled as React.ElementType} w={6} h={6} color={noteColor.muted} mb={2} />
                                            <Text fontSize="sm" color={noteColor.ink}>
                                                No notes yet
                                            </Text>
                                            <Text fontSize="xs" color={noteColor.muted} mt={1}>
                                                Add a private note below
                                            </Text>
                                        </Box>
                                    </Flex>
                                );
                            })()
                        ) : (
                            /* Messages View */
                            (() => {
                                const messages = [...notes].filter(n => (n.type ?? 'message') === 'message')
                                    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                                return messages.length > 0 ? (
                                    messages.map((note, index) => {
                                    const isOwnNote = note.userId === sessionContext?.userId;
                                    const isEditing = editingNoteId === note._id;
                                    const showDayDivider = index === 0 ||
                                        !isSameDay(messages[index - 1].timestamp.toString(), note.timestamp.toString());

                                    // --- Message bubble ---
                                    return (
                                        <React.Fragment key={`${note.timestamp}-${index}`}>
                                        {showDayDivider && (
                                            <Flex w="100%" justify="center" py={2}>
                                                <Text
                                                    fontSize="11px"
                                                    color="gray.500"
                                                    fontWeight="medium"
                                                    bg="white"
                                                    borderWidth="1px"
                                                    borderColor="gray.200"
                                                    borderRadius="full"
                                                    px={3}
                                                    py={0.5}
                                                >
                                                    {formatDayDivider(note.timestamp.toString())}
                                                </Text>
                                            </Flex>
                                        )}
                                        {(() => {
                                            const filled = false;
                                            return (
                                        <HStack
                                            alignSelf={isOwnNote ? 'flex-end' : 'flex-start'}
                                            align="flex-end"
                                            spacing={2}
                                            maxW={isEditing ? '100%' : '80%'}
                                            w={isEditing ? '100%' : undefined}
                                            flexDirection={isOwnNote ? 'row-reverse' : 'row'}
                                        >
                                        <PresenceAvatar
                                            name={note.user}
                                            user={presenceByUserId.get(note.userId) ?? null}
                                            size="xs"
                                            bg={isOwnNote ? '#0071E3' : 'gray.400'}
                                            alignEnd={isOwnNote}
                                        />
                                        <VStack
                                            align={isOwnNote ? 'flex-end' : 'flex-start'}
                                            spacing={1}
                                            minW={0}
                                            w={isEditing ? '100%' : undefined}
                                        >
                                        <Box
                                            w={isEditing ? '100%' : undefined}
                                            bg={filled ? '#0071E3' : 'white'}
                                            color={filled ? 'white' : 'gray.700'}
                                            px={4}
                                            py={2.5}
                                            borderRadius="2xl"
                                            borderBottomRightRadius={isOwnNote && !isEditing ? 'md' : '2xl'}
                                            borderBottomLeftRadius={!isOwnNote ? 'md' : '2xl'}
                                            borderWidth={filled ? 0 : '1px'}
                                            borderColor={isEditing ? '#0071E3' : 'gray.200'}
                                            boxShadow="none"
                                            position="relative"
                                        >
                                            <HStack justify="space-between" mb={0.5}>
                                                <Text fontSize="11px" fontWeight="semibold" color={filled ? 'whiteAlpha.800' : '#0071E3'}>
                                                    {isOwnNote ? 'You' : note.user}
                                                </Text>
                                                <HStack spacing={0}>
                                                    {isOwnNote && !isEditing && (
                                                        <>
                                                            <IconButton
                                                                aria-label="Edit message"
                                                                icon={<Icon as={TbPencil as React.ElementType} w={3} h={3} />}
                                                                size="xs"
                                                                variant="ghost"
                                                                color="gray.400"
                                                                minW="auto"
                                                                h="auto"
                                                                p={1}
                                                                onClick={() => startEditingNote(note._id, note.note)}
                                                                _hover={{ color: '#0071E3', bg: 'gray.100' }}
                                                            />
                                                            <Popover placement="top" isLazy>
                                                                <PopoverTrigger>
                                                                    <IconButton
                                                                        aria-label="Delete message"
                                                                        icon={<Icon as={TbTrash as React.ElementType} w={3} h={3} />}
                                                                        size="xs"
                                                                        variant="ghost"
                                                                        color="gray.400"
                                                                        minW="auto"
                                                                        h="auto"
                                                                        p={1}
                                                                        _hover={{ color: 'red.500', bg: 'gray.100' }}
                                                                    />
                                                                </PopoverTrigger>
                                                                <Portal>
                                                                    <PopoverContent w="auto" minW="120px">
                                                                        <PopoverBody p={2}>
                                                                            <VStack spacing={1}>
                                                                                <Text fontSize="xs" color="gray.600">Delete this message?</Text>
                                                                                <HStack spacing={2}>
                                                                                    <Button size="xs" colorScheme="red" onClick={() => handleDeleteNote(note._id, 'message')}>
                                                                                        Delete
                                                                                    </Button>
                                                                                </HStack>
                                                                            </VStack>
                                                                        </PopoverBody>
                                                                    </PopoverContent>
                                                                </Portal>
                                                            </Popover>
                                                        </>
                                                    )}
                                                </HStack>
                                            </HStack>
                                            {isEditing ? (
                                                <VStack spacing={2} align="stretch">
                                                    <Textarea
                                                        value={editingNoteText}
                                                        onChange={(e) => setEditingNoteText(e.target.value)}
                                                        onKeyDown={(e) => handleNoteKeyDown(e, true)}
                                                        size="sm"
                                                        resize="none"
                                                        minH="60px"
                                                        fontSize="sm"
                                                        autoFocus
                                                        isDisabled={updateOrderDetails.isPending}
                                                    />
                                                    <HStack spacing={2} justify="flex-end">
                                                        <Button
                                                            size="xs"
                                                            variant="outlineGray"
                                                            leftIcon={<Icon as={TbX as React.ElementType} />}
                                                            onClick={cancelEditingNote}
                                                            isDisabled={updateOrderDetails.isPending}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            size="xs"
                                                            colorScheme="blue"
                                                            leftIcon={<Icon as={TbCheck as React.ElementType} />}
                                                            onClick={handleEditNote}
                                                            isLoading={updateOrderDetails.isPending}
                                                        >
                                                            Save
                                                        </Button>
                                                    </HStack>
                                                </VStack>
                                            ) : (
                                                <Text
                                                    fontSize="sm"
                                                    whiteSpace="pre-wrap"
                                                    lineHeight="1.5"
                                                >
                                                    {renderNoteText(note.note, mentionHighlightNames, filled ? '#C0DCFA' : '#0071E3')}
                                                </Text>
                                            )}
                                        </Box>
                                        {!isEditing && (
                                            <Text fontSize="10px" color="gray.400" px={1}>
                                                {formatMessageTime(note.timestamp.toString())}
                                            </Text>
                                        )}
                                        </VStack>
                                        </HStack>
                                            );
                                        })()}
                                        </React.Fragment>
                                    );
                                    })
                                ) : (
                                    <Flex flex="1" align="center" justify="center" direction="column" py={8}>
                                        <Icon as={TbMessageCircle as React.ElementType} w={8} h={8} color="gray.300" mb={2} />
                                        <Text fontSize="sm" color="gray.400" textAlign="center">
                                            No messages yet
                                        </Text>
                                        <Text fontSize="xs" color="gray.400" textAlign="center" mt={1}>
                                            Send a message below
                                        </Text>
                                    </Flex>
                                );
                            })()
                        )}
                        <div ref={notesEndRef} />
                    </VStack>

                    {/* Compose Area - only for order chat */}
                    {!isSupplierMode && (
                        <Box
                            mt="auto"
                            p={4}
                            bg="white"
                            borderTopWidth="1px"
                            borderColor="border.default"
                            flexShrink={0}
                        >
                            {/* Compose mode toggle: Message vs private Note to self */}
                            <HStack spacing={1} mb={2}>
                                <Button
                                    size="xs"
                                    variant={composeMode === 'message' ? 'solid' : 'ghost'}
                                    bg={composeMode === 'message' ? '#0071E3' : undefined}
                                    color={composeMode === 'message' ? 'white' : 'gray.600'}
                                    // Already-selected mode holds its fill on hover — there is
                                    // nothing to activate, so darkening reads as a second state.
                                    _hover={{ bg: composeMode === 'message' ? '#0071E3' : 'gray.100' }}
                                    leftIcon={<Icon as={TbMessageCircle as React.ElementType} w={3} h={3} />}
                                    onClick={() => setComposeMode('message')}
                                    borderRadius="md"
                                    fontWeight="medium"
                                >
                                    Message
                                </Button>
                                <Button
                                    size="xs"
                                    variant={composeMode === 'note' ? 'solid' : 'ghost'}
                                    bg={composeMode === 'note' ? noteColor.solid : undefined}
                                    color={composeMode === 'note' ? noteColor.ink : 'gray.600'}
                                    _hover={{ bg: composeMode === 'note' ? noteColor.hover : 'gray.100' }}
                                    leftIcon={<Icon as={TbNote as React.ElementType} w={3} h={3} />}
                                    onClick={() => setComposeMode('note')}
                                    borderRadius="md"
                                    fontWeight="medium"
                                >
                                    Note to self
                                </Button>
                            </HStack>

                            <Box position="relative" display="flex" flexDirection="column" justifyContent="flex-end">
                                {/* @mention dropdown */}
                                {showMentionMenu && composeMode === 'message' && filteredMentionUsers.length > 0 && (
                                    <Box
                                        position="absolute"
                                        bottom="100%"
                                        left={0}
                                        right={0}
                                        mb={1}
                                        bg="white"
                                        borderWidth="1px"
                                        borderColor="gray.200"
                                        borderRadius="lg"
                                        boxShadow="none"
                                        maxH="160px"
                                        overflow="auto"
                                        zIndex={10}
                                    >
                                        {filteredMentionUsers.map((user, index) => (
                                            <Box
                                                key={user._id}
                                                px={3}
                                                py={2}
                                                cursor="pointer"
                                                bg={index === selectedMentionIndex ? 'gray.100' : undefined}
                                                _hover={{ bg: 'gray.100' }}
                                                onClick={() => handleMentionSelect(user)}
                                                ref={index === selectedMentionIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
                                            >
                                                <HStack spacing={2} justify="space-between">
                                                    <Box>
                                                        <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                                            {user.displayName}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.500">
                                                            {positionLabel(user.role, user.position)}
                                                        </Text>
                                                    </Box>
                                                    {index === selectedMentionIndex && (
                                                        <Text fontSize="xs" color="gray.400" flexShrink={0}>
                                                            tab
                                                        </Text>
                                                    )}
                                                </HStack>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                <Box
                                    borderWidth="1px"
                                    borderRadius="xl"
                                    borderColor={composeMode === 'note' ? noteColor.border : 'gray.300'}
                                    bg={composeMode === 'note' ? noteColor.paper : 'gray.50'}
                                    _hover={{ borderColor: composeMode === 'note' ? noteColor.deep : 'gray.400' }}
                                    _focusWithin={{
                                        borderColor: composeMode === 'note' ? noteColor.deep : '#0071E3',
                                        boxShadow: 'none',
                                        bg: composeMode === 'note' ? noteColor.hover : 'white',
                                    }}
                                    transition="all 0.2s"
                                    overflow="hidden"
                                >
                                    {composeMode === 'message' && mentionedUsers.length > 0 && (
                                        <Wrap spacing={1} px={3} pt={2} pb={0}>
                                            {mentionedUsers.map(user => (
                                                <WrapItem key={user._id}>
                                                    <Tag
                                                        size="sm"
                                                        borderRadius="full"
                                                        variant="solid"
                                                        bg="#0071E3"
                                                        color="white"
                                                    >
                                                        <TagLabel fontSize="xs">{user.displayName}</TagLabel>
                                                        <TagCloseButton onClick={() => removeMention(user._id)} />
                                                    </Tag>
                                                </WrapItem>
                                            ))}
                                        </Wrap>
                                    )}
                                    <Textarea
                                        ref={noteInputRef}
                                        as={ResizeTextarea}
                                        placeholder={composeMode === 'message' ? 'Send a message...' : 'Note to self...'}
                                        value={newNoteText}
                                        onChange={handleInputChange}
                                        onKeyDown={(e) => handleNoteKeyDown(e, false)}
                                        resize="none"
                                        minH="80px"
                                        maxRows={8}
                                        fontSize="sm"
                                        border="none"
                                        borderRadius="0"
                                        bg="transparent"
                                        pr="48px"
                                        pb="36px"
                                        overflow="auto"  
                                        sx={{
                                            '&::-webkit-scrollbar': {
                                              display: 'none',
                                            },
                                            msOverflowStyle: 'none',  // IE and Edge
                                            scrollbarWidth: 'none',  // Firefox
                                          }}
                                        isDisabled={updateOrderDetails.isPending}
                                        color={composeMode === 'note' ? noteColor.ink : undefined}
                                        _placeholder={{ color: composeMode === 'note' ? noteColor.muted : 'gray.400' }}
                                        _hover={{ borderColor: 'transparent' }}
                                        _focus={{ borderColor: 'transparent', boxShadow: 'none' }}
                                    />
                                </Box>
                                <IconButton
                                    aria-label="Send"
                                    icon={<Icon as={TbArrowUp as React.ElementType} w={4} h={4} />}
                                    position="absolute"
                                    bottom="8px"
                                    right="8px"
                                    zIndex={2}
                                    size="sm"
                                    borderRadius="full"
                                    bg={composeMode === 'note' ? noteColor.ink : '#0071E3'}
                                    color="white"
                                    _hover={{
                                        bg: composeMode === 'note' ? noteColor.inkHover : '#3544a0',
                                    }}
                                    _disabled={{
                                        bg: composeMode === 'note' ? noteColor.ink : '#0071E3',
                                        opacity: 0.6,
                                        cursor: 'not-allowed',
                                    }}
                                    onClick={handleAddNote}
                                    isLoading={updateOrderDetails.isPending}
                                    isDisabled={!newNoteText.trim() && mentionedUsers.length === 0}
                                    minW="28px"
                                    h="28px"
                                />
                            </Box>
                            <Text fontSize="xs" color={composeMode === 'note' ? noteColor.muted : 'gray.400'} mt={1.5} ml={1}>
                                {composeMode === 'message' ? 'Press Ctrl+Enter to send • Type @ to mention' : 'Press Ctrl+Enter to save'}
                            </Text>
                        </Box>
                    )}
                </Box>
    );
};

/** Drawer wrapper around OrderChatPanel -- used when opened as a standalone drawer */
const OrderChatDrawer = ({
    isOpen,
    onClose,
    hideOverlay = false,
    ...panelProps
}: OrderChatDrawerProps) => {
    useForceSidebarCollapsed(isOpen);
    return (
        <Drawer
            isOpen={isOpen}
            placement="right"
            onClose={onClose}
            size="md"
            blockScrollOnMount={!hideOverlay}
            trapFocus={!hideOverlay}
            closeOnOverlayClick={!hideOverlay}
        >
            {!hideOverlay && <DrawerOverlay />}
            <DrawerContent
                data-partbay-drawer=""
                overflow="hidden"
                bg="white"
                containerProps={hideOverlay ? SIDE_DRAWER_CONTAINER_PROPS : undefined}
                motionProps={INSTANT_SIDE_DRAWER_MOTION_PROPS}
            >
                <OrderChatPanel {...panelProps} onClose={onClose} />
            </DrawerContent>
        </Drawer>
    );
};

export default OrderChatDrawer;
